import { format, isValid, parse } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import * as React from 'react'
import { useMemo } from 'react'
import { Matcher } from 'react-day-picker'

import { useIsMobile } from '../../hooks/use-mobile'
import { DATE_FIELD_FORMAT as DATE_FORMAT } from '../../lib/dateField'
import { cn } from '../../utils/cn'
import { Calendar } from './calendar'
import { InputGroup, InputGroupAddon, InputGroupInput } from './input-group'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet'

const PLACEHOLDER = 'MM/DD/YYYY'

function formatInputValue(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export const DateInput = React.memo(function DateInput({
  label,
  value,
  onChange,
  inputProps,
  hidden,
  defaultMonth,
  startMonth,
  endMonth,
  errorMessage,
  description,
  inputGroupClassName,
}: {
  label?: string
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  inputProps?: React.ComponentProps<'input'>
  hidden?: Matcher | Matcher[] | undefined
  defaultMonth?: Date | undefined
  startMonth?: Date | undefined
  endMonth?: Date | undefined
  errorMessage?: string
  /** Shown under the field and read as its description, e.g. the range `hidden` allows. */
  description?: string
  /** Sizes the input box alone, so the description and error under it keep the full width. */
  inputGroupClassName?: string
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const descriptionId = React.useId()
  const errorId = React.useId()
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(false)
  const [textValue, setTextValue] = React.useState(value ? format(value, DATE_FORMAT) : '')
  const [isFocused, setIsFocused] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [month, setMonth] = React.useState(
    defaultMonth ||
      (typeof hidden === 'object' && 'after' in hidden ? hidden.after : undefined) ||
      value ||
      new Date()
  )

  React.useEffect(() => {
    if (!isFocused) {
      setTextValue(value ? format(value, DATE_FORMAT) : '')
    }
  }, [value, isFocused])

  const derivedEndMonth = useMemo(() => {
    if (endMonth) return endMonth
    const matchers = Array.isArray(hidden) ? hidden : hidden ? [hidden] : []
    for (const matcher of matchers) {
      if (typeof matcher === 'object' && 'after' in matcher) return matcher.after
    }
    return undefined
  }, [endMonth, hidden])

  function isDateHidden(date: Date): boolean {
    if (!hidden) return false
    const matchers = Array.isArray(hidden) ? hidden : [hidden]
    for (const matcher of matchers) {
      if (typeof matcher === 'object' && 'after' in matcher && matcher.after && date > matcher.after)
        return true
      if (typeof matcher === 'object' && 'before' in matcher && matcher.before && date < matcher.before)
        return true
    }
    return false
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputValue(e.target.value)
    setTextValue(formatted)
    setError(null)

    if (formatted.length === 10) {
      const parsed = parse(formatted, DATE_FORMAT, new Date())
      if (isValid(parsed) && !isDateHidden(parsed)) {
        onChange(parsed)
        setMonth(parsed)
      } else if (isValid(parsed) && isDateHidden(parsed) && errorMessage) {
        setError(errorMessage)
      }
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (next && value && !isDateHidden(value)) setMonth(value)
    setOpen(next)
  }

  const handleBlur = () => {
    setIsFocused(false)
    if (textValue && textValue.length === 10) {
      const parsed = parse(textValue, DATE_FORMAT, new Date())
      if (isValid(parsed) && !isDateHidden(parsed)) {
        setError(null)
        onChange(parsed)
      } else {
        if (isDateHidden(parsed) && errorMessage) {
          setError(errorMessage)
        }
        setTextValue(value ? format(value, DATE_FORMAT) : '')
      }
    } else if (textValue === '') {
      setError(null)
      onChange(undefined)
    } else {
      setTextValue(value ? format(value, DATE_FORMAT) : '')
    }
  }

  const calendar = (
    <Calendar
      mode="single"
      selected={value}
      month={month}
      onMonthChange={setMonth}
      captionLayout="dropdown"
      startMonth={startMonth}
      endMonth={derivedEndMonth}
      hidden={hidden}
      onSelect={(date) => {
        // react-day-picker fires undefined when re-clicking the selected day — keep the value.
        if (date) onChange(date)
        setOpen(false)
      }}
    />
  )

  return (
    <>
      <Popover open={open && !isMobile} onOpenChange={handleOpenChange}>
        <div className="flex flex-col">
          <InputGroup className={cn('bg-surface h-10', inputGroupClassName)}>
            <InputGroupInput
              value={textValue}
              placeholder={label || PLACEHOLDER}
              className="text-left"
              {...inputProps}
              aria-describedby={
                [inputProps?.['aria-describedby'], description && descriptionId, error && errorId]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
              onChange={handleTextChange}
              onFocus={() => {
                setIsFocused(true)
                // Clicking back in is how they fix it; the next check of what they type says if it's still wrong.
                setError(null)
              }}
              onBlur={handleBlur}
            />
            <InputGroupAddon align="inline-start">
              <PopoverTrigger asChild>
                <button
                  ref={triggerRef}
                  type="button"
                  aria-label="Open calendar"
                  className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={inputProps?.disabled}
                >
                  <CalendarIcon className="size-5" />
                </button>
              </PopoverTrigger>
            </InputGroupAddon>
          </InputGroup>
          {description && (
            <p id={descriptionId} className="text-muted-foreground mt-1 text-xs">
              {description}
            </p>
          )}
          {error && (
            <p id={errorId} className="text-error mt-1 text-xs">
              {error}
            </p>
          )}
        </div>
        <PopoverContent
          className="max-h-(--radix-popover-content-available-height) w-auto overflow-x-hidden overflow-y-auto p-0"
          align="start"
          collisionPadding={8}
        >
          {calendar}
        </PopoverContent>
      </Popover>
      <Sheet open={open && isMobile} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          className="max-h-dvh items-center gap-0 overflow-y-auto pb-6"
          onCloseAutoFocus={(e) => {
            // Refocusing the input would pop the phone keyboard back up.
            e.preventDefault()
            triggerRef.current?.focus()
          }}
        >
          {/* A landscape phone has no room for the title above a six-week month. */}
          <SheetHeader className="w-full [@media(max-height:480px)]:sr-only">
            <SheetTitle>Choose a date</SheetTitle>
          </SheetHeader>
          {calendar}
        </SheetContent>
      </Sheet>
    </>
  )
})

export function DOBInput({
  value,
  onChange,
  inputProps,
  minAge = 18,
  maxAge = 70,
}: {
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  inputProps?: React.ComponentProps<'input'>
  minAge?: number
  maxAge?: number
}) {
  const minDate = useMemo(() => new Date(new Date().setFullYear(new Date().getFullYear() - minAge)), [minAge])
  const maxDate = useMemo(() => new Date(new Date().setFullYear(new Date().getFullYear() - maxAge)), [maxAge])
  const defaultMonth = useMemo(() => new Date(new Date().setFullYear(new Date().getFullYear() - 35)), [])
  const hidden = useMemo(() => [{ after: minDate }, { before: maxDate }], [minDate, maxDate])

  return (
    <DateInput
      value={value}
      onChange={onChange}
      inputProps={inputProps}
      defaultMonth={defaultMonth}
      startMonth={maxDate}
      hidden={hidden}
      errorMessage={`Must be between ${minAge} and ${maxAge} years old`}
    />
  )
}
